/**
 * BrightPath - ExpenseCard Component
 * BP-04-04: Expenses Frontend
 */

import React from 'react';
import { Link } from 'react-router-dom';
import {
  Stethoscope,
  Brain,
  Accessibility,
  Pill,
  Car,
  Home,
  Scale,
  Shield,
  MoreHorizontal,
  Calendar,
  User,
  Receipt,
  Tag,
  CheckCircle,
  Clock,
  AlertCircle,
} from 'lucide-react';
import type { Expense, ExpenseCategory } from '../types';
import { EXPENSE_CATEGORIES, CLAIM_STATUS_LABELS } from '../types';
import { formatCurrency, formatDate } from '@/core/lib/utils';

interface ExpenseCardProps {
  expense: Expense;
  isSelected?: boolean;
  onSelect?: (id: string) => void;
  showChild?: boolean;
  compact?: boolean;
}

const categoryIcons: Record<ExpenseCategory, React.ReactNode> = {
  medical: <Stethoscope className="w-5 h-5" />,
  therapy: <Brain className="w-5 h-5" />,
  equipment: <Accessibility className="w-5 h-5" />,
  medication: <Pill className="w-5 h-5" />,
  transportation: <Car className="w-5 h-5" />,
  home_modification: <Home className="w-5 h-5" />,
  legal: <Scale className="w-5 h-5" />,
  insurance: <Shield className="w-5 h-5" />,
  other: <MoreHorizontal className="w-5 h-5" />,
};

export function ExpenseCard({
  expense,
  isSelected = false,
  onSelect,
  showChild = true,
  compact = false,
}: ExpenseCardProps) {
  const categoryConfig = EXPENSE_CATEGORIES[expense.category];
  const claimStatusConfig = expense.claimStatus ? CLAIM_STATUS_LABELS[expense.claimStatus] : null;

  const handleSelect = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onSelect?.(expense.id);
  };

  if (compact) {
    return (
      <Link
        to={`/family/expenses/${expense.id}`}
        className={`
          block bg-white rounded-lg border transition-all duration-200
          hover:shadow-md hover:-translate-y-0.5
          ${isSelected ? 'border-sage ring-2 ring-sage/20' : 'border-warm-gray/10'}
        `}
      >
        <div className="p-4 flex items-center gap-4">
          {onSelect && (
            <button
              onClick={handleSelect}
              className={`
                w-5 h-5 rounded border-2 flex-shrink-0 transition-colors
                ${isSelected ? 'bg-sage border-sage' : 'border-warm-gray/30 hover:border-sage'}
              `}
            >
              {isSelected && <CheckCircle className="w-4 h-4 text-white" />}
            </button>
          )}

          <div
            className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: `${categoryConfig.color}20`, color: categoryConfig.color }}
          >
            {categoryIcons[expense.category]}
          </div>

          <div className="flex-1 min-w-0">
            <p className="font-medium text-navy truncate">{expense.description}</p>
            <p className="text-sm text-warm-gray">
              {formatDate(expense.expenseDate)} • {categoryConfig.label}
            </p>
          </div>

          <div className="text-right flex-shrink-0">
            <p className="font-semibold text-navy">{formatCurrency(expense.amount)}</p>
            {expense.outOfPocket !== expense.amount && (
              <p className="text-sm text-warm-gray">
                OOP: {formatCurrency(expense.outOfPocket)}
              </p>
            )}
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/family/expenses/${expense.id}`}
      className={`
        block bg-white rounded-xl border transition-all duration-200
        hover:shadow-lg hover:-translate-y-1
        ${isSelected ? 'border-sage ring-2 ring-sage/20' : 'border-warm-gray/10'}
      `}
    >
      <div className="p-5">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 mb-4">
          <div className="flex items-start gap-3">
            {onSelect && (
              <button
                onClick={handleSelect}
                className={`
                  w-5 h-5 mt-1 rounded border-2 flex-shrink-0 transition-colors
                  ${isSelected ? 'bg-sage border-sage' : 'border-warm-gray/30 hover:border-sage'}
                `}
              >
                {isSelected && <CheckCircle className="w-4 h-4 text-white" />}
              </button>
            )}

            <div
              className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: `${categoryConfig.color}15`, color: categoryConfig.color }}
            >
              {categoryIcons[expense.category]}
            </div>

            <div className="min-w-0">
              <h3 className="font-semibold text-navy text-lg truncate">{expense.description}</h3>
              <div className="flex items-center gap-2 mt-1">
                <span
                  className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
                  style={{ backgroundColor: `${categoryConfig.color}15`, color: categoryConfig.color }}
                >
                  {categoryConfig.label}
                </span>
                {expense.isTaxDeductible && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-sage/10 text-sage-dark">
                    Tax Deductible
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="text-right flex-shrink-0">
            <p className="text-2xl font-bold text-navy">{formatCurrency(expense.amount)}</p>
            {expense.insuranceAmount && expense.insuranceAmount > 0 && (
              <p className="text-sm text-warm-gray mt-1">
                Insurance: {formatCurrency(expense.insuranceAmount)}
              </p>
            )}
          </div>
        </div>

        {/* Details */}
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div className="flex items-center gap-2 text-warm-gray">
            <Calendar className="w-4 h-4" />
            <span>{formatDate(expense.expenseDate)}</span>
          </div>

          {expense.vendorName && (
            <div className="flex items-center gap-2 text-warm-gray">
              <Receipt className="w-4 h-4" />
              <span className="truncate">{expense.vendorName}</span>
            </div>
          )}

          {showChild && expense.child && (
            <div className="flex items-center gap-2 text-warm-gray">
              <User className="w-4 h-4" />
              <span>{expense.child.firstName}</span>
            </div>
          )}

          {claimStatusConfig && (
            <div className="flex items-center gap-2">
              {expense.claimStatus === 'approved' ? (
                <CheckCircle className="w-4 h-4" style={{ color: claimStatusConfig.color }} />
              ) : expense.claimStatus === 'denied' ? (
                <AlertCircle className="w-4 h-4" style={{ color: claimStatusConfig.color }} />
              ) : (
                <Clock className="w-4 h-4" style={{ color: claimStatusConfig.color }} />
              )}
              <span style={{ color: claimStatusConfig.color }}>{claimStatusConfig.label}</span>
            </div>
          )}
        </div>

        {/* Tags */}
        {expense.tags && expense.tags.length > 0 && (
          <div className="flex items-center gap-2 mt-3 pt-3 border-t border-warm-gray/10">
            <Tag className="w-4 h-4 text-warm-gray" />
            <div className="flex flex-wrap gap-1">
              {expense.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-2 py-0.5 rounded-full text-xs bg-cream-dark text-navy-light"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Out of Pocket highlight */}
        {expense.outOfPocket !== expense.amount && (
          <div className="mt-3 pt-3 border-t border-warm-gray/10 flex justify-between items-center">
            <span className="text-sm text-warm-gray">Out of Pocket</span>
            <span className="font-semibold text-terracotta">
              {formatCurrency(expense.outOfPocket)}
            </span>
          </div>
        )}
      </div>
    </Link>
  );
}

export default ExpenseCard;
